<template>
  <!--
    Náhľad dokumentu priamo na stránke: PDF (s textovou vrstvou), Word .docx, Excel / CSV, PowerPoint .pptx (text snímok),
    textové súbory a obrázky. Označený text sa dá poslať do diskusie („Komentovať výber“) a citát z diskusie
    sa dá v dokumente znova nájsť a zvýrazniť (metóda zvyraznit).
  -->
  <div ref="koren" class="dokument-viewer" :class="{ 'dokument-viewer--vyplnit': vyplnit }">
    <div ref="scroll" class="dokument-viewer__scroll" @mouseup="onVyber" @keyup="onVyber" @scroll="tlacidlo = null">
      <div v-if="stav === 'nacitavam'" class="dokument-viewer__state">
        <v-progress-circular indeterminate size="26" width="2" />
        <span>Načítavam dokument…</span>
      </div>
      <div v-else-if="stav === 'chyba'" class="dokument-viewer__state">
        <v-icon icon="mdi-alert-circle-outline" size="40" />
        <span>{{ chyba }}</span>
      </div>
      <div v-else-if="stav === 'nepodporovany'" class="dokument-viewer__state">
        <v-icon icon="mdi-file-outline" size="40" />
        <span class="dokument-viewer__state-title">Náhľad súboru .{{ pripona }} sa nedá zobraziť</span>
        <span>Prehliadač tento formát neotvorí. Stiahnite si ho alebo nahrajte revíziu ako PDF alebo DOCX.</span>
      </div>

      <div ref="obsah" class="dokument-viewer__obsah" :class="`dokument-viewer__obsah--${druh}`">
        <pre v-if="druh === 'text' && stav === 'hotovo'" class="dok-text">{{ text }}</pre>

        <img v-else-if="druh === 'obrazok' && obrazokUrl" :src="obrazokUrl" :alt="nazov" class="dok-obrazok" />

        <div v-else-if="druh === 'tabulka' && stav === 'hotovo'" class="dok-tabulka">
          <div v-if="harky.length > 1" class="dok-tabulka__harky" role="tablist">
            <button
              v-for="(harok, index) in harky"
              :key="harok.nazov"
              type="button"
              role="tab"
              class="dok-tabulka__harok"
              :class="{ 'is-active': index === aktivnyHarok }"
              :aria-selected="index === aktivnyHarok"
              @click="aktivnyHarok = index"
            >
              {{ harok.nazov }}
            </button>
          </div>
          <div v-if="harky[aktivnyHarok]" class="dok-tabulka__wrap" :data-strana="aktivnyHarok + 1">
            <table>
              <tbody>
                <tr v-for="(riadok, r) in harky[aktivnyHarok].riadky" :key="r">
                  <th class="dok-tabulka__cislo">{{ r + 1 }}</th>
                  <td v-for="(bunka, c) in riadok" :key="c">{{ bunka }}</td>
                </tr>
              </tbody>
            </table>
            <p v-if="harky[aktivnyHarok].skratene" class="dok-tabulka__pozn">
              Zobrazených je prvých {{ MAX_RIADKOV }} riadkov. Celý hárok nájdete v stiahnutom súbore.
            </p>
          </div>
        </div>

        <div v-else-if="druh === 'prezentacia' && stav === 'hotovo'" class="dok-prezentacia">
          <section v-for="snimka in snimky" :key="snimka.cislo" class="dok-snimka" :data-strana="snimka.cislo">
            <div class="dok-snimka__cislo">Snímka {{ snimka.cislo }}</div>
            <p v-for="(odsek, i) in snimka.odseky" :key="i" class="dok-snimka__odsek">{{ odsek }}</p>
            <p v-if="!snimka.odseky.length" class="dok-snimka__prazdna">Snímka bez textu</p>
          </section>
          <p class="dok-prezentacia__pozn">Prezentácia je zobrazená ako text snímok, bez obrázkov a grafiky.</p>
        </div>

        <!-- PDF a DOCX sa vykresľujú priamo do DOM (pdf.js, docx-preview) -->
        <div v-show="druh === 'pdf' || druh === 'docx'" ref="imperativ" class="dok-imperativ"></div>
      </div>
    </div>

    <button
      v-if="tlacidlo && komentovanie"
      type="button"
      class="dokument-viewer__komentovat"
      :style="{ top: `${tlacidlo.top}px`, left: `${tlacidlo.left}px` }"
      @mousedown.prevent
      @click="komentovat"
    >
      <v-icon icon="mdi-message-outline" size="16" />
      Komentovať výber
    </button>
  </div>
</template>

<script>
// Najviac strán PDF a riadkov tabuľky v náhľade (väčšie súbory sa sťahujú)
const MAX_STRAN = 80;
const MAX_RIADKOV = 1000;
const MAX_CITACIA = 1000;
const ZVYRAZNENIE = 'diskusia-kotva';

const DRUHY = {
  pdf: 'pdf',
  docx: 'docx',
  xlsx: 'tabulka',
  xls: 'tabulka',
  ods: 'tabulka',
  csv: 'tabulka',
  pptx: 'prezentacia',
  txt: 'text',
  md: 'text',
  log: 'text',
  json: 'text',
  xml: 'text',
  jpg: 'obrazok',
  jpeg: 'obrazok',
  png: 'obrazok',
  gif: 'obrazok',
  webp: 'obrazok',
  bmp: 'obrazok',
  svg: 'obrazok'
};

// Text súboru: UTF-8, inak Windows-1250 (staršie súbory zo slovenských Windows)
const dekodujText = (buffer) => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch (e) {
    return new TextDecoder('windows-1250').decode(buffer);
  }
};

export default {
  name: 'DokumentViewer',

  props: {
    // Obsah súboru (načítaný cez prihlásené API)
    blob: {
      type: Blob,
      default: null
    },
    // Pôvodný názov súboru - podľa prípony sa vyberie spôsob zobrazenia
    nazov: {
      type: String,
      default: ''
    },
    // Ponúknuť „Komentovať výber“ (len tam, kde má záznam diskusiu)
    komentovanie: {
      type: Boolean,
      default: true
    },
    // Vyplniť celú výšku rodiča (globálny náhľad) namiesto pevnej výšky
    vyplnit: {
      type: Boolean,
      default: false
    }
  },

  emits: ['komentovat', 'vykreslene'],

  data() {
    return {
      MAX_RIADKOV,
      stav: 'nacitavam',
      chyba: '',
      text: '',
      obrazokUrl: '',
      harky: [],
      aktivnyHarok: 0,
      snimky: [],
      tlacidlo: null,
      renderId: 0,
      vykreslenie: Promise.resolve()
    };
  },

  computed: {
    pripona() {
      const casti = String(this.nazov || '').toLowerCase().split('.');
      return casti.length > 1 ? casti.pop() : '';
    },

    druh() {
      return DRUHY[this.pripona] || 'nepodporovany';
    }
  },

  watch: {
    blob() {
      this.vykreslenie = this.vykreslit();
    }
  },

  mounted() {
    this.vykreslenie = this.vykreslit();
  },

  beforeUnmount() {
    this.renderId += 1;
    this.uvolnitObrazok();
    this.zrusitZvyraznenie();
  },

  methods: {
    async vykreslit() {
      const id = ++this.renderId;
      this.stav = 'nacitavam';
      this.tlacidlo = null;
      this.text = '';
      this.harky = [];
      this.aktivnyHarok = 0;
      this.snimky = [];
      this.uvolnitObrazok();
      this.zrusitZvyraznenie();
      if (this.$refs.imperativ) this.$refs.imperativ.innerHTML = '';

      if (!this.blob) return;
      if (this.druh === 'nepodporovany') {
        this.stav = 'nepodporovany';
        return;
      }

      try {
        if (this.druh === 'pdf') await this.vykreslitPdf(id);
        else if (this.druh === 'docx') await this.vykreslitDocx(id);
        else if (this.druh === 'tabulka') await this.vykreslitTabulku(id);
        else if (this.druh === 'prezentacia') await this.vykreslitPrezentaciu(id);
        else if (this.druh === 'text') this.text = dekodujText(await this.blob.arrayBuffer());
        else if (this.druh === 'obrazok') this.obrazokUrl = URL.createObjectURL(this.blob);

        if (id !== this.renderId) return;
        this.stav = 'hotovo';
        await this.$nextTick();
        this.$emit('vykreslene');
      } catch (error) {
        if (id !== this.renderId) return;
        console.error('Chyba pri zobrazení dokumentu:', error);
        this.chyba = 'Dokument sa nepodarilo zobraziť. Môže byť poškodený alebo chránený heslom.';
        this.stav = 'chyba';
      }
    },

    async vykreslitPdf(id) {
      const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf');
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.js', import.meta.url).toString();
      }

      const data = new Uint8Array(await this.blob.arrayBuffer());
      // isEvalSupported: false - pdf.js nesmie spúšťať kód z písiem v PDF
      const pdf = await pdfjsLib.getDocument({ data, isEvalSupported: false }).promise;
      const kontajner = this.$refs.imperativ;
      const sirka = Math.max((this.$refs.scroll ? this.$refs.scroll.clientWidth : 800) - 48, 320);
      const ratio = window.devicePixelRatio || 1;

      for (let cislo = 1; cislo <= Math.min(pdf.numPages, MAX_STRAN); cislo++) {
        if (id !== this.renderId) return;
        const page = await pdf.getPage(cislo);
        const zaklad = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: Math.min(Math.max(sirka / zaklad.width, 0.5), 2) });

        const strana = document.createElement('div');
        strana.className = 'pdf-strana';
        strana.dataset.strana = String(cislo);
        strana.style.width = `${viewport.width}px`;
        strana.style.height = `${viewport.height}px`;
        strana.style.setProperty('--scale-factor', String(viewport.scale));

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const vrstva = document.createElement('div');
        vrstva.className = 'textLayer';
        strana.append(canvas, vrstva);
        kontajner.append(strana);

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
          transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null
        }).promise;
        await pdfjsLib.renderTextLayer({
          textContentSource: page.streamTextContent(),
          container: vrstva,
          viewport,
          textDivs: []
        }).promise;
      }

      if (pdf.numPages > MAX_STRAN) {
        const pozn = document.createElement('p');
        pozn.className = 'dok-pozn';
        pozn.textContent = `Zobrazených je prvých ${MAX_STRAN} strán z ${pdf.numPages}. Celý dokument nájdete v stiahnutom súbore.`;
        kontajner.append(pozn);
      }
    },

    async vykreslitDocx(id) {
      const { renderAsync } = await import(/* webpackChunkName: "docx-preview" */ 'docx-preview');
      if (id !== this.renderId) return;
      const kontajner = this.$refs.imperativ;
      await renderAsync(this.blob, kontajner, kontajner, {
        className: 'docx',
        inWrapper: true,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        useBase64URL: true
      });
      // Strany pre odkaz z diskusie („str. 2“)
      kontajner.querySelectorAll('section.docx').forEach((sekcia, index) => {
        sekcia.dataset.strana = String(index + 1);
      });
    },

    async vykreslitTabulku(id) {
      const XLSX = await import(/* webpackChunkName: "xlsx" */ 'xlsx');
      const buffer = await this.blob.arrayBuffer();
      const zosit = this.pripona === 'csv'
        ? XLSX.read(dekodujText(buffer), { type: 'string' })
        : XLSX.read(buffer, { type: 'array' });
      if (id !== this.renderId) return;

      this.harky = zosit.SheetNames.map((meno) => {
        const riadky = XLSX.utils.sheet_to_json(zosit.Sheets[meno], { header: 1, defval: '', raw: false, blankrows: false });
        return { nazov: meno, riadky: riadky.slice(0, MAX_RIADKOV), skratene: riadky.length > MAX_RIADKOV };
      });
    },

    async vykreslitPrezentaciu(id) {
      const JSZip = (await import(/* webpackChunkName: "jszip" */ 'jszip')).default;
      const zip = await JSZip.loadAsync(this.blob);
      const cislo = (meno) => Number((meno.match(/slide(\d+)\.xml$/) || [])[1] || 0);
      const subory = Object.keys(zip.files)
        .filter(meno => /^ppt\/slides\/slide\d+\.xml$/.test(meno))
        .sort((a, b) => cislo(a) - cislo(b));

      const snimky = [];
      for (const meno of subory) {
        const xml = await zip.file(meno).async('string');
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        const odseky = Array.from(doc.getElementsByTagName('a:p'))
          .map(p => Array.from(p.getElementsByTagName('a:t')).map(t => t.textContent).join('').trim())
          .filter(Boolean);
        snimky.push({ cislo: cislo(meno), odseky });
      }
      if (id !== this.renderId) return;
      this.snimky = snimky;
    },

    // Po označení textu ukáže tlačidlo „Komentovať výber“ pod výberom
    onVyber() {
      if (!this.komentovanie) return;
      requestAnimationFrame(() => {
        const vyber = window.getSelection();
        if (!vyber || vyber.isCollapsed || !vyber.rangeCount || !this.$refs.obsah) {
          this.tlacidlo = null;
          return;
        }
        const range = vyber.getRangeAt(0);
        if (!this.$refs.obsah.contains(range.commonAncestorContainer)) {
          this.tlacidlo = null;
          return;
        }
        const citacia = vyber.toString().replace(/\s+/g, ' ').trim();
        if (citacia.length < 2) {
          this.tlacidlo = null;
          return;
        }

        const rect = range.getBoundingClientRect();
        const box = this.$refs.koren.getBoundingClientRect();
        const left = Math.min(Math.max(rect.left - box.left + rect.width / 2, 90), box.width - 90);
        const top = Math.min(rect.bottom - box.top + 8, box.height - 44);
        this.tlacidlo = {
          top,
          left,
          citacia: citacia.slice(0, MAX_CITACIA),
          strana: this.stranaUzla(range.startContainer)
        };
      });
    },

    komentovat() {
      if (!this.tlacidlo) return;
      this.$emit('komentovat', { citacia: this.tlacidlo.citacia, strana: this.tlacidlo.strana });
      const vyber = window.getSelection();
      if (vyber) vyber.removeAllRanges();
      this.tlacidlo = null;
    },

    stranaUzla(uzol) {
      const element = uzol && uzol.nodeType === Node.TEXT_NODE ? uzol.parentElement : uzol;
      const strana = element && element.closest ? element.closest('[data-strana]') : null;
      return strana ? Number(strana.dataset.strana) || null : null;
    },

    /**
     * Nájde citovaný text v dokumente, zvýrazní ho a posunie naň (medzery sa pri hľadaní ignorujú)
     * @param {string} citacia
     * @param {number|null} strana - pri tabuľke číslo hárku
     * @returns {Promise<boolean>} či sa text našiel
     */
    async zvyraznit(citacia, strana = null) {
      await this.vykreslenie;
      if (this.druh === 'tabulka' && strana && this.harky[strana - 1]) {
        this.aktivnyHarok = strana - 1;
        await this.$nextTick();
      }

      const hladane = String(citacia || '').replace(/\s+/g, '');
      const koren = this.$refs.obsah;
      if (!hladane || !koren) return false;

      const uzly = [];
      const pozicie = [];
      let kompakt = '';
      const walker = document.createTreeWalker(koren, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const uzol = walker.currentNode;
        const text = uzol.nodeValue;
        for (let i = 0; i < text.length; i++) {
          if (!/\s/.test(text[i])) {
            kompakt += text[i];
            uzly.push(uzol);
            pozicie.push(i);
          }
        }
      }

      const index = kompakt.indexOf(hladane);
      if (index < 0) return false;
      const koniec = index + hladane.length - 1;

      const range = document.createRange();
      range.setStart(uzly[index], pozicie[index]);
      range.setEnd(uzly[koniec], pozicie[koniec] + 1);

      this.zrusitZvyraznenie();
      if (window.CSS && CSS.highlights && typeof window.Highlight === 'function') {
        CSS.highlights.set(ZVYRAZNENIE, new window.Highlight(range));
      } else {
        const vyber = window.getSelection();
        vyber.removeAllRanges();
        vyber.addRange(range);
      }

      const element = uzly[index].parentElement;
      if (element) element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    },

    zrusitZvyraznenie() {
      if (window.CSS && CSS.highlights) CSS.highlights.delete(ZVYRAZNENIE);
    },

    uvolnitObrazok() {
      if (this.obrazokUrl) URL.revokeObjectURL(this.obrazokUrl);
      this.obrazokUrl = '';
    }
  }
};
</script>

<style scoped>
.dokument-viewer {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dokument-viewer__scroll {
  height: calc(100dvh - 250px);
  min-height: 420px;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 24px;
  background: var(--surface-soft);
}

/* Globálny náhľad: prehliadač vyplní zvyšok výšky */
.dokument-viewer--vyplnit {
  flex: 1;
}

.dokument-viewer--vyplnit .dokument-viewer__scroll {
  flex: 1;
  height: auto;
  min-height: 0;
}

.dokument-viewer__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 300px;
  padding: 24px;
  text-align: center;
  font-size: 0.9375rem;
  color: var(--text-muted);
}

.dokument-viewer__state-title {
  font-weight: 650;
  color: var(--text-primary);
}

.dokument-viewer__komentovat {
  position: absolute;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: var(--radius-pill);
  background: #1d1d1f;
  box-shadow: var(--shadow-pop);
  font-size: 0.8125rem;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  transform: translateX(-50%);
  transition: background-color var(--duration-fast) ease;
}

.dokument-viewer__komentovat:hover {
  background: var(--brand-ruby);
}

/* Text */
.dok-text {
  max-width: 90ch;
  margin: 0 auto;
  padding: 28px 32px;
  border-radius: var(--radius-control);
  background: var(--surface);
  box-shadow: var(--shadow-card);
  font-family: var(--font-mono);
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Obrázok */
.dokument-viewer__obsah--obrazok {
  display: flex;
  justify-content: center;
}

.dok-obrazok {
  max-width: 100%;
  height: auto;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-card);
  background: var(--surface);
}

/* PDF: strany pod sebou, nad plátnom priehľadná textová vrstva na označovanie */
.dok-imperativ :deep(.pdf-strana) {
  position: relative;
  margin: 0 auto 16px;
  background: #ffffff;
  box-shadow: var(--shadow-card);
}

.dok-imperativ :deep(.pdf-strana canvas) {
  display: block;
}

.dok-imperativ :deep(.textLayer) {
  position: absolute;
  inset: 0;
  overflow: hidden;
  line-height: 1;
  text-align: initial;
  text-size-adjust: none;
  forced-color-adjust: none;
  transform-origin: 0 0;
  z-index: 2;
}

.dok-imperativ :deep(.textLayer span),
.dok-imperativ :deep(.textLayer br) {
  position: absolute;
  color: transparent;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
}

.dok-imperativ :deep(.textLayer span.markedContent) {
  top: 0;
  height: 0;
}

.dok-imperativ :deep(.textLayer ::selection) {
  background: rgba(26, 111, 156, 0.3);
}

.dok-imperativ :deep(.textLayer br::selection) {
  background: transparent;
}

.dok-imperativ :deep(.textLayer .endOfContent) {
  display: block;
  position: absolute;
  inset: 100% 0 0;
  z-index: -1;
  cursor: default;
  user-select: none;
}

.dok-imperativ :deep(.dok-pozn) {
  margin: 8px 0 0;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--text-muted);
}

/* DOCX (docx-preview): strany na sivom pozadí ako v textovom editore */
.dok-imperativ :deep(.docx-wrapper) {
  padding: 0;
  background: transparent;
}

.dok-imperativ :deep(.docx-wrapper > section.docx) {
  margin: 0 auto 16px;
  box-shadow: var(--shadow-card);
}

/* Tabuľka */
.dok-tabulka__harky {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
}

.dok-tabulka__harok {
  height: 30px;
  padding: 0 12px;
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.dok-tabulka__harok.is-active {
  background: var(--surface);
  color: var(--text-primary);
  box-shadow: var(--shadow-card);
}

.dok-tabulka__wrap {
  overflow: auto;
  border-radius: var(--radius-control);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.dok-tabulka table {
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.dok-tabulka td,
.dok-tabulka th {
  padding: 6px 10px;
  border: 1px solid var(--border-subtle);
  white-space: nowrap;
  color: var(--text-primary);
}

.dok-tabulka__cislo {
  background: var(--surface-soft);
  font-weight: 500;
  color: var(--text-muted) !important;
  user-select: none;
}

.dok-tabulka__pozn {
  margin: 0;
  padding: 10px 12px;
  font-size: 0.8125rem;
  color: var(--text-muted);
}

/* Prezentácia: snímky ako karty s textom */
.dok-prezentacia {
  max-width: 820px;
  margin: 0 auto;
}

.dok-snimka {
  margin-bottom: 16px;
  padding: 24px 28px;
  border-radius: var(--radius-control);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.dok-snimka__cislo {
  margin-bottom: 10px;
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.02em;
  color: var(--text-muted);
  user-select: none;
}

.dok-snimka__odsek {
  margin: 0 0 8px;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--text-primary);
}

.dok-snimka__odsek:first-of-type {
  font-size: 1.0625rem;
  font-weight: 650;
}

.dok-snimka__prazdna,
.dok-prezentacia__pozn {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
}

@media (max-width: 959px) {
  .dokument-viewer__scroll {
    height: auto;
    max-height: 75vh;
    padding: 12px;
  }

  .dokument-viewer--vyplnit .dokument-viewer__scroll {
    max-height: none;
  }

  .dok-text {
    padding: 16px;
  }
}
</style>

<style>
/* Zvýraznenie citátu z diskusie (CSS Custom Highlight API; ::highlight nemôže byť v scoped štýle) */
::highlight(diskusia-kotva) {
  background-color: rgba(246, 181, 90, 0.55);
  color: inherit;
}
</style>
